import { LeaderboardRowSkeleton } from "../ui/LeaderboardRowSkeleton"
import { Skeleton } from "../ui/Skeleton"

/**
 * Skeleton for WeekView component.
 * Used by home page and past week pages during loading.
 * Matches the WeekView structure exactly to prevent layout shift.
 */
export function WeekViewSkeleton() {
	return (
		<div className="grid min-w-0 gap-4 sm:gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
			{/* Featured - shows first on mobile, sidebar on desktop */}
			<aside className="lg:order-2">
				<div className="rounded-2xl border border-breakout-secondary/20 bg-bg-card p-3 text-center shadow-lg sm:rounded-3xl sm:p-5 lg:p-6">
					{/* Countdown badge - matches CountdownBadge size="sm" in FeaturedMarketCard */}
					<div className="mb-2 flex justify-center sm:mb-4">
						<Skeleton className="h-5 w-20 rounded-full" />
					</div>
					{/* Title: image + text - matches flex-col layout in FeaturedMarketCard */}
					<div className="flex flex-col items-center gap-1.5 sm:gap-2">
						<Skeleton className="h-7 w-8 rounded sm:h-9 sm:w-11" />
						<Skeleton className="h-5 w-40 sm:h-[30px] sm:w-56" />
					</div>
					{/* Contenders section */}
					<div className="mt-2 sm:mt-4">
						{/* Mobile: avatars with gap-2.5, size-9 squares */}
						<div className="flex justify-center gap-2.5 sm:hidden">
							{[0, 1, 2, 3, 4].map(idx => (
								<Skeleton className="size-9 rounded-md" key={idx} />
							))}
						</div>
						{/* Desktop: full chips with gap-1.5, size-8 avatars */}
						<div className="hidden flex-wrap justify-center gap-1.5 sm:flex">
							{[0, 1, 2, 3, 4].map(idx => (
								<div
									className="flex items-center gap-1.5 rounded-full border border-border-subtle bg-bg-card py-1 pr-2.5 pl-1 shadow-sm"
									key={idx}
								>
									<Skeleton className="size-8 rounded-md" />
									<Skeleton className="h-5 w-12" />
								</div>
							))}
						</div>
						{/* Volume text - text-sm (14px, ~20px line-height) mobile, sm:text-base (16px, ~24px) desktop */}
						<Skeleton className="mx-auto mt-3 h-5 w-16 sm:mt-4 sm:h-6 sm:w-20" />
					</div>
				</div>
			</aside>

			{/* Leaderboard - main content, shows second on mobile */}
			<section className="min-w-0 space-y-2 lg:order-1">
				{/* Header: title + separator + date + countdown + IPFS links */}
				<div className="flex flex-wrap items-center justify-between gap-2 px-1">
					<div className="flex items-center gap-2">
						{/* "CT Mindshare" - text-lg font-bold */}
						<Skeleton className="h-6 w-24" />
						{/* Separator dot */}
						<Skeleton className="size-1" />
						{/* Date range link - text-sm */}
						<Skeleton className="h-4 w-28" />
						{/* CountdownBadge size="sm" - text-xs with py-0.5 px-1.5 */}
						<Skeleton className="h-5 w-16 rounded-full" />
					</div>
					{/* IPFS links - icon only on mobile, icon+text on desktop */}
					<div className="flex items-center gap-2">
						<Skeleton className="size-3 sm:h-3 sm:w-10" />
						<Skeleton className="size-3 sm:h-3 sm:w-8" />
					</div>
				</div>

				{/* Column Headers - matches actual text-[10px]/lg:text-xs sizing */}
				{/* Labels: Creator, Mindshare, Liquidity (Liq) */}
				<div className="flex items-center gap-2.5 px-3 lg:gap-5 lg:px-6">
					{/* Creator */}
					<div className="flex-1">
						<Skeleton className="h-2.5 w-12 lg:h-3 lg:w-14" />
					</div>
					{/* Mindshare / Share */}
					<div className="shrink-0">
						<Skeleton className="h-2.5 w-8 lg:h-3 lg:w-16" />
					</div>
					{/* Liquidity / Liq */}
					<div className="w-14 shrink-0 pl-3 lg:w-20 lg:pl-4">
						<Skeleton className="mx-auto h-2.5 w-6 lg:h-3 lg:w-14" />
					</div>
				</div>

				{/* Rows */}
				<div className="min-w-0 space-y-2">
					{Array.from({ length: 10 }, (_, i) => i + 1).map(rank => (
						<LeaderboardRowSkeleton key={rank} />
					))}
				</div>
			</section>
		</div>
	)
}
